import type { z } from 'zod/v4'

import type { ApisType } from '@/legacy-src/store/createApis'

import { getValidAccessToken, refreshAccessToken } from './refreshAccessToken'

interface FetchAuthorizedInit extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>
}

interface FetchAuthorizedParseInit<T> extends FetchAuthorizedInit {
  schema: z.ZodType<T>
}

export class FetchAuthorizedError extends Error {
  constructor(
    path: string,
    public readonly response: Response,
  ) {
    super(`Failed to fetch ${path}: ${response.status} ${response.statusText}`)
  }
}

export const isFetchAuthorizedError = (error: unknown): error is FetchAuthorizedError =>
  error instanceof FetchAuthorizedError

const fetchWithAccessToken = async (path: string, api: ApisType['api'], init?: FetchAuthorizedInit) => {
  const accessToken = await getValidAccessToken(api)

  const res = await fetch(path, { ...init, headers: { ...init?.headers, Authorization: accessToken ?? '' } })

  if (res.status !== 401) {
    return res
  }

  const refreshed = await refreshAccessToken(api)
  if (!refreshed) {
    return res
  }

  return await fetch(path, { ...init, headers: { ...init?.headers, Authorization: `Bearer ${refreshed}` } })
}

export async function fetchAuthorized<T>(
  path: string,
  api: ApisType['api'],
  init: FetchAuthorizedParseInit<T>,
): Promise<T>
export async function fetchAuthorized(path: string, api: ApisType['api'], init?: FetchAuthorizedInit): Promise<Response>
export async function fetchAuthorized<T>(
  path: string,
  api: ApisType['api'],
  { schema, ...init }: Partial<FetchAuthorizedParseInit<T>> = {},
) {
  const res = await fetchWithAccessToken(path, api, init)

  if (!res.ok) {
    throw new FetchAuthorizedError(path, res)
  }

  if (!schema) {
    return res
  }

  return schema.parse(await res.json())
}
